import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Head from 'next/head';
import compose from 'recompose/compose';
import dynamic from 'next/dynamic';

import {reduxForm, SubmissionError} from 'redux-form';
import {updateSettings} from '../../reducers/auth';
import {openSnackbar} from '../../reducers/snackbar';

import ErrorBoundary from '../../components/ErrorBoundary';
import Snackbar from '../../components/Snackbar/Snackbar';
import Tabs from '../../components/Tabs';
import Container from '@material-ui/core/Container';

import {Dashboard as DashboardLayout} from '../../layouts';
import sendSegment from '../../libs/segment';

const CompanyForm = dynamic(() => import('./components/CompanyForm'));
const BrandingForm = dynamic(() => import('./components/BrandingForm'));
const AdvancedForm = dynamic(() => import('./components/AdvancedForm'));
const StargateForm = dynamic(() => import('./components/StargateForm'));
const ApiKeysForm = dynamic(() => import('./components/ApiKeys'), {ssr: false});
const ReportsForm = dynamic(() => import('./components/ReportsForm'), {ssr: false});

class Settings extends Component {
    state = {
        tab: this.props.router.query.tab || 'company',
    };

    componentWillReceiveProps(nextProps, nextContext) {
        const {user} = this.props;
        if (!user.id && nextProps.user && nextProps.user.company) {
            this.autofillFields(nextProps.user);
        }
    }

    componentDidMount() {
        const {user} = this.props;
        if (user && user.id && user.company) {
            this.autofillFields(user);
        }
    }

    autofillFields = (user) => {
        const {initialize} = this.props;
        const company = user.company || {};
        const settings = user.settings || {};
        const twilio = user.twilio || {};

        if (!settings.autohost_stargate_relay_default_hours) {
            settings.autohost_stargate_relay_default_hours = '24';
        }

        if (!settings.notification_late_verification_warning_hours) {
            settings.notification_late_verification_warning_hours = '18';
        }

        initialize({settings, company, twilio});
    };

    handleSubmit = async (form) => {
        const {openSnackbar, updateSettings, user} = this.props;

        // create settings object to update
        const data = {
            company: {
                ...form.company,
            },
            settings: {
                ...form.settings,
            },
            twilio: {
                ...form.twilio,
            }
        };

        // set empty fields to null before saving
        if (form.settings) {
            [
                'sheetsu_blacklist_url',
                'sheetsu_blacklist_fingerprint_field',
                'sheetsu_blacklist_phone_field',
                'sheetsu_blacklist_name_field',
                'sheetsu_blacklist_email_field',
                'notification_late_verification_warning_hours',
                'notification_slack_webhook',
                'notification_email',
                'guest_portal_custom_domain',
                'guest_portal_webhook_url',
                'screening_webhook_url',
                'webhook_authorization',
                'operto_portal_base_url',
                'enable_daily_digest_email_reservations',
                'sso_shared_secret_key',
            ].forEach((key) => {
                if (!form.settings[key] || form.settings[key] === '') {
                    data.settings[key] = null;
                }
            });
        }
        if (form.company) {
            [
                'name',
                'website_url',
                'email',
                'phone',
                'faq_url',
                'color_primary',
                'color_secondary',
                'logo_url',
                'logo_contrast_url',
            ].forEach((key) => {
                if (!form.company[key] || form.company[key] === '') {
                    data.company[key] = null;
                }
            });
        }
        if (form.twilio) {
            [
                'api_key',
                'api_secret',
                'number',
                'message_service_id',
            ].forEach((key) => {
                if (!form.twilio[key] || form.twilio[key] === '') {
                    data.twilio[key] = null;
                }
            });
        }

        // save settings
        try {
            await updateSettings(data);
        } catch (e) {
            openSnackbar(typeof e === 'string' ? e : 'Failed to update settings', null, {variant: 'error'});
            throw new SubmissionError({_error: e});
        }

        // update form with new settings
        this.autofillFields(data);

        // show confirmation message to user
        openSnackbar('Settings updated', null, {variant: 'success'});

        // log event with Segment
        sendSegment('track', {
            name: 'Updated Settings',
            data: {
                name: user.name,
                email: user.email,
                ...data,
            },
        });
    };

    handleTab = (event, tab) => {
        const {user, router} = this.props;
        this.setState({tab}, () => {
            typeof window !== 'undefined' && router.replace(`/settings?tab=${tab}`);
            this.autofillFields(user);
        });
    };

    renderTab = (tab) => {
        const {user, openSnackbar, router} = this.props;
        const props = {
            user,
            openSnackbar,
            router,
            onSubmit: this.handleSubmit,
        };
        let component;
        if (tab === 'company') {
            component = <CompanyForm {...props} />;
        }
        if (tab === 'branding') {
            component = <BrandingForm {...props} />;
        }
        if (tab === 'advanced') {
            component = <AdvancedForm {...props} />;
        }
        if (tab === 'stargate') {
            component = <StargateForm {...props} />;
        }
        if (tab === 'api') {
            component = <ApiKeysForm {...props} />;
        }
        if (tab === 'reports') {
            component = <ReportsForm {...props} />;
        }
        if (component) {
            return <ErrorBoundary>{component}</ErrorBoundary>;
        }
    };

    render() {
        const {tab} = this.state;
        return (
            <DashboardLayout title="Settings">
                <Container maxWidth="md">
                    <Head>
                        <title>Settings</title>
                    </Head>

                    <Tabs
                        onClick={this.handleTab}
                        tab={tab}
                        tabs={[
                            {label: 'Company', value: 'company'},
                            {label: 'Branding', value: 'branding'},
                            {label: 'Advanced', value: 'advanced'},
                            {label: 'Stargate', value: 'stargate'},
                            {label: 'API', value: 'api'},
                            ...((this.props.flagsmithState.flags?.portal_schedule_reports_tab?.enabled && [
                                {label: 'Reports', value: 'reports'},
                            ]) ||
                                []),
                        ]}
                    />

                    {this.renderTab(tab)}
                </Container>

                <Snackbar />
            </DashboardLayout>
        );
    }
}

Settings.propTypes = {
    // connected
    updateSettings: PropTypes.func.isRequired,
    openSnackbar: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,

    // redux-form
    initialize: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    return {
        user: state.auth.user,
    };
};

const ReduxForm = {
    form: 'settings',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
};

export {Settings};
export default compose(connect(mapStateToProps, {updateSettings, openSnackbar}), reduxForm(ReduxForm))(Settings);
